import React from 'react'
import { Button, Col, Form, Row } from 'react-bootstrap'
import DentalDataCard from './DentalDataCard'
import {
  treatmentTableDefaultPageSize,
  treatmentTablePageSizeOptions,
} from './constants'

const treatmentTableBodyHeight = 220

const actionIconButtonStyle = {
  width: 28,
  height: 28,
  padding: 0,
  border: 0,
  borderRadius: '50%',
  background: 'transparent',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',
}

const actionIconStyle = {
  fontSize: 16,
}

const onActionIconMouseEnter = (event) => {
  event.currentTarget.style.background = '#f5f5f5'
}

const onActionIconMouseLeave = (event) => {
  event.currentTarget.style.background = 'transparent'
}

const TreatmentTable = ({
  data = [],
  page = 0,
  pageSize = treatmentTableDefaultPageSize,
  totalCount = 0,
  isLoading = false,
  onAdd,
  onEdit,
  onDelete,
  onPageChange,
  onPageSizeChange,
  title = 'หัตถการ',
}) => {
  const totalPage = Math.max(Math.ceil(totalCount / pageSize), 1)
  const pageData = data
  const startRow = totalCount ? page * pageSize + 1 : 0
  const endRow = Math.min((page + 1) * pageSize, totalCount)
  const canPreviousPage = page > 0
  const canNextPage = page < totalPage - 1
  const cellStyle = {
    minWidth: 120,
    maxWidth: 300,
    paddingTop: 6,
    paddingBottom: 6,
    textOverflow: 'clip',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  }
  const orderCellStyle = {
    minWidth: 56,
    maxWidth: 56,
    width: 56,
    paddingTop: 6,
    paddingBottom: 6,
    whiteSpace: 'nowrap',
  }
  const actionCellStyle = {
    minWidth: 76,
    maxWidth: 76,
    width: 76,
    paddingTop: 4,
    paddingBottom: 4,
    whiteSpace: 'nowrap',
  }

  const handlePageSizeChange = (event) => {
    onPageSizeChange(Number(event.target.value))
  }

  return (
    <DentalDataCard title={title}>
      <Row className="mb-2">
        <Col className="d-flex justify-content-end">
          <Button
            className="btn-icon mx-1"
            variant="outline-primary"
            onClick={onAdd}
          >
            <i className="feather icon-plus mx-1" />
            เพิ่ม
          </Button>
        </Col>
      </Row>
      <div className="position-relative">
        <div className="table-responsive">
          <table className="table table-sm mb-0">
            <thead>
              <tr>
                <th style={actionCellStyle}>ACTIONS</th>
                <th style={orderCellStyle}>ลำดับ</th>
                <th style={cellStyle}>หัตถการ</th>
                <th style={cellStyle}>จำนวนซี่ฟัน</th>
                <th style={cellStyle}>ซี่ฟัน</th>
                <th style={cellStyle}>ค่าบริการ</th>
                <th style={cellStyle}>ICD10</th>
                <th style={cellStyle}>ICD9</th>
                <th style={cellStyle}>ผู้รักษา</th>
                <th style={cellStyle}>ผู้ช่วย</th>
              </tr>
            </thead>
            <tbody>
              {!isLoading && pageData.length ? (
                pageData.map((rowData, index) => (
                  <tr key={`${rowData.id}-${index}`}>
                    <td style={actionCellStyle}>
                      <button
                        type="button"
                        aria-label="Edit"
                        style={{ ...actionIconButtonStyle, marginRight: 4 }}
                        onMouseEnter={onActionIconMouseEnter}
                        onMouseLeave={onActionIconMouseLeave}
                        onClick={() => onEdit(rowData)}
                      >
                        <i
                          className="feather icon-edit-2 text-warning"
                          style={actionIconStyle}
                        />
                      </button>
                      <button
                        type="button"
                        aria-label="Delete"
                        style={actionIconButtonStyle}
                        onMouseEnter={onActionIconMouseEnter}
                        onMouseLeave={onActionIconMouseLeave}
                        onClick={() => onDelete(rowData)}
                      >
                        <i
                          className="feather icon-trash-2 text-danger"
                          style={actionIconStyle}
                        />
                      </button>
                    </td>
                    <td style={orderCellStyle}>
                      {page * pageSize + index + 1}
                    </td>
                    <td style={cellStyle}>{rowData.dentalService}</td>
                    <td style={cellStyle}>{rowData.treatedTeethCountText}</td>
                    <td style={cellStyle}>{rowData.toothDisplay}</td>
                    <td style={cellStyle}>{rowData.serviceChargeText}</td>
                    <td style={cellStyle}>{rowData.icd10}</td>
                    <td style={cellStyle}>{rowData.icd9}</td>
                    <td style={cellStyle}>{rowData.doctor}</td>
                    <td style={cellStyle}>{rowData.helper}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={10}
                    style={{ height: treatmentTableBodyHeight }}
                  />
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {(isLoading || !pageData.length) && (
          <div
            className="position-absolute d-flex align-items-center justify-content-center"
            style={{
              left: 0,
              right: 0,
              bottom: 0,
              height: treatmentTableBodyHeight,
              pointerEvents: 'none',
            }}
          >
            {isLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm mr-2"
                  role="status"
                />
                กำลังโหลดข้อมูล...
              </>
            ) : (
              'ไม่พบข้อมูล'
            )}
          </div>
        )}
      </div>
      <div className="d-flex justify-content-end align-items-center p-2">
        <Form.Control
          as="select"
          size="sm"
          value={pageSize}
          onChange={handlePageSizeChange}
          style={{ width: 90 }}
        >
          {treatmentTablePageSizeOptions.map((option) => (
            <option key={option} value={option}>
              {option} rows
            </option>
          ))}
        </Form.Control>
        <span className="mx-3">
          {startRow}-{endRow} of {totalCount}
        </span>
        <button
          type="button"
          className="btn btn-link p-0 mx-2 text-dark"
          disabled={!canPreviousPage}
          onClick={() => onPageChange(0)}
        >
          <i className="feather icon-chevrons-left" />
        </button>
        <button
          type="button"
          className="btn btn-link p-0 mx-2 text-dark"
          disabled={!canPreviousPage}
          onClick={() => onPageChange(page - 1)}
        >
          <i className="feather icon-chevron-left" />
        </button>
        <button
          type="button"
          className="btn btn-link p-0 mx-2 text-dark"
          disabled={!canNextPage}
          onClick={() => onPageChange(page + 1)}
        >
          <i className="feather icon-chevron-right" />
        </button>
        <button
          type="button"
          className="btn btn-link p-0 mx-2 text-dark"
          disabled={!canNextPage}
          onClick={() => onPageChange(totalPage - 1)}
        >
          <i className="feather icon-chevrons-right" />
        </button>
      </div>
    </DentalDataCard>
  )
}


export default TreatmentTable
